import { useEffect, useRef, useState } from "react";
import { MicIcon, MicOffIcon, PhoneIcon, PhoneOffIcon } from "lucide-react";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@ui/button";

import { SadieVoiceCallStatus } from "@/hooks/useSadieVoiceCall";
import { SadieVoiceCaption } from "@/lib/sadieVoiceSimulator";
import { VoiceGap } from "@/lib/voiceGapTracker";
import { cn } from "@/lib/utils";

const STATUS_LABEL: Record<SadieVoiceCallStatus, string> = {
  idle: "Not connected",
  connecting: "Connecting…",
  active: "On the call",
  ending: "Hanging up…",
  ended: "Call ended",
  failed: "Call failed",
};

const STATUS_DOT: Record<SadieVoiceCallStatus, string> = {
  idle: "bg-slate-300",
  connecting: "bg-amber-400 animate-pulse",
  active: "bg-emerald-500",
  ending: "bg-amber-400",
  ended: "bg-slate-400",
  failed: "bg-red-500",
};

const SPEAKER_LABEL = {
  worker: "You",
  sadie: "Sadie",
  text: "Text to you (not sent)",
} as const;

const SPEAKER_BUBBLE = {
  worker: "bg-primary-200",
  sadie: "bg-slate-100",
  text: "border border-emerald-200 bg-emerald-50",
} as const;

const formatElapsed = (ms: number) => {
  const seconds = Math.floor(ms / 1000);
  return `${String(Math.floor(seconds / 60))}:${String(seconds % 60).padStart(2, "0")}`;
};

const seconds = (ms: number) => `${(ms / 1000).toFixed(1)}s`;

// You stop talking -> Sadie starts, for the last turn and the call so far.
// Realtime also splits the last gap into turn detection and the reply.
const GapReadout = ({ gaps }: { gaps: VoiceGap[] }) => {
  const last = gaps.at(-1);
  if (!last) return null;
  const sorted = gaps.map((g) => g.totalMs).sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  return (
    <Row className="items-center gap-4 border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-600">
      <Text className="font-medium text-slate-700">Response gap</Text>
      <Text>
        Last <span className="font-mono">{seconds(last.totalMs)}</span>
        {last.turnDetectionMs !== undefined ? (
          <span className="text-slate-500">
            {" "}
            (turn detection{" "}
            <span className="font-mono">{seconds(last.turnDetectionMs)}</span> ·
            reply{" "}
            <span className="font-mono">
              {seconds(last.totalMs - last.turnDetectionMs)}
            </span>
            )
          </span>
        ) : null}
      </Text>
      <Text>
        Median <span className="font-mono">{seconds(median)}</span> over{" "}
        {gaps.length} {gaps.length === 1 ? "turn" : "turns"}
      </Text>
    </Row>
  );
};

const CallTimer = ({ startedAt }: { startedAt: number }) => {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const id = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      clearInterval(id);
    };
  }, []);
  return (
    <Text className="font-mono text-sm tabular-nums text-slate-500">
      {formatElapsed(now - startedAt)}
    </Text>
  );
};

export const SadieVoiceSimulatorCall = ({
  canStart,
  captions,
  error,
  gaps,
  isMuted,
  liveActions = [],
  onHangUp,
  onStart,
  onToggleMute,
  phoneCallButton,
  postCall,
  sessionId,
  startedAt,
  status,
  warning,
}: {
  canStart: boolean;
  captions: SadieVoiceCaption[];
  error: string | null;
  gaps: VoiceGap[];
  isMuted: boolean;
  // Live only: mid-call notes the page can push.
  liveActions?: { label: string; onClick: () => void }[];
  onHangUp: () => void;
  onStart: () => void;
  onToggleMute: () => void;
  // Save For Phone Call, beside the call buttons.
  phoneCallButton?: React.ReactNode;
  // Shown once a call has ended.
  postCall?: React.ReactNode;
  sessionId: string | null;
  startedAt: number | null;
  status: SadieVoiceCallStatus;
  warning: string | null;
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const inCall =
    status === "connecting" || status === "active" || status === "ending";

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [captions]);

  return (
    // min-w-0: without it a long unbroken line (the post-call JSON) widens
    // the pane past the window instead of wrapping.
    <Column className="min-h-0 min-w-0 flex-1">
      <Row className="items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
        <Row className="items-center gap-3">
          <span
            className={cn("h-2.5 w-2.5 rounded-full", STATUS_DOT[status])}
          />
          <Text className="text-sm font-medium text-slate-700">
            {STATUS_LABEL[status]}
          </Text>
          {status === "active" && startedAt ? (
            <CallTimer startedAt={startedAt} />
          ) : null}
          {sessionId ? (
            <Text className="font-mono text-xs text-slate-400">
              {sessionId}
            </Text>
          ) : null}
        </Row>
        <Row className="items-center gap-2">
          {phoneCallButton}
          {inCall ? (
            <>
              <Button
                disabled={status !== "active"}
                onClick={onToggleMute}
                size="sm"
                variant="outline"
              >
                {isMuted ? (
                  <MicOffIcon className="mr-1.5 h-4 w-4" />
                ) : (
                  <MicIcon className="mr-1.5 h-4 w-4" />
                )}
                {isMuted ? "Unmute" : "Mute"}
              </Button>
              <Button
                disabled={status === "ending"}
                onClick={onHangUp}
                size="sm"
                variant="destructive"
              >
                <PhoneOffIcon className="mr-1.5 h-4 w-4" />
                Hang up
              </Button>
            </>
          ) : (
            <Button disabled={!canStart} onClick={onStart} size="sm">
              <PhoneIcon className="mr-1.5 h-4 w-4" />
              {status === "idle" ? "Start call" : "New call"}
            </Button>
          )}
        </Row>
      </Row>

      <GapReadout gaps={gaps} />

      {status === "active" && liveActions.length ? (
        <Row className="items-center gap-2 border-b border-slate-200 px-4 py-2">
          <Text className="text-xs font-medium text-slate-500">Push</Text>
          {liveActions.map((action) => (
            <Button
              className="h-7 text-xs"
              key={action.label}
              onClick={action.onClick}
              size="sm"
              variant="outline"
            >
              {action.label}
            </Button>
          ))}
        </Row>
      ) : null}

      {error ? (
        <Text className="border-b border-red-100 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </Text>
      ) : null}
      {warning && !error ? (
        <Text className="border-b border-amber-100 bg-amber-50 px-4 py-2 text-sm text-amber-800">
          OpenAI reported: {warning}
        </Text>
      ) : null}

      <Column className="min-h-0 flex-1 gap-3 overflow-y-auto p-4">
        {captions.length === 0 ? (
          <Text className="m-auto max-w-sm text-center text-sm text-slate-500">
            {inCall
              ? "Listening. What you and Sadie say shows up here."
              : "Start a call and talk to Sadie as the worker. Use headphones so she doesn't hear herself."}
          </Text>
        ) : (
          captions.map((caption) =>
            caption.speaker === "event" ? (
              <Text
                className="self-center text-xs text-slate-400"
                key={caption.key}
              >
                {caption.text}
              </Text>
            ) : (
              <Column
                className={cn(
                  "max-w-[75%] gap-0.5",
                  caption.speaker === "worker"
                    ? "items-end self-end"
                    : "self-start",
                )}
                key={caption.key}
              >
                <Text className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                  {SPEAKER_LABEL[caption.speaker]}
                </Text>
                <Text
                  className={cn(
                    "whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-sm text-slate-800",
                    SPEAKER_BUBBLE[caption.speaker],
                  )}
                >
                  {caption.text.trim() || "…"}
                </Text>
              </Column>
            ),
          )
        )}
        <div ref={bottomRef} />
      </Column>
      {status === "ended" && captions.length ? postCall : null}
    </Column>
  );
};
